import axiosInstance from "../../../store/api";

const BASE = "/config/corebase";
let activeWorkspace = null;
export const setCrmWorkspace = (id) => { activeWorkspace = id; };
export const crmStorageKey = (key, workspaceId = activeWorkspace, viewerId = currentUserId()) => `${key}_${viewerId}_${workspaceId || viewerId}`;
const options = (workspaceId = activeWorkspace) => ({ timeout: 20000, headers: workspaceId ? { 'X-CRM-Workspace': workspaceId } : {} });
export const CRM_ENTITY_KEYS = [
  "leads", "contactosHistorial", "tareasFollowup", "ventas",
  "pagosCredito", "resumenDiario", "historialEtapas", "historialEdicionesLead",
  "oportunidadesExpansion", "historialAsignaciones",
  "snapshotsPipeline",
];

const uuid = () => window.crypto?.randomUUID?.() || `crm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const clone = (value) => JSON.parse(JSON.stringify(value));
const currentUserId = () => {
  try { return JSON.parse(localStorage.getItem("user") || "null")?._id || "anon"; }
  catch { return "anon"; }
};
const queueKey = () => crmStorageKey('corebase_crm_tx_v1');
const readQueue = (key = queueKey()) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch { return []; }
};
const writeQueue = (queue, key = queueKey()) => localStorage.setItem(key, JSON.stringify(queue));

export function diffCrmStates(previous = {}, next = {}, isOwner = true) {
  const changes = { protocolVersion: 2, upserts: {}, deletes: {}, bases: {} };
  if (isOwner && previous.config !== next.config && JSON.stringify(previous.config || {}) !== JSON.stringify(next.config || {})) {
    changes.config = clone(next.config || {});
    changes.configBase = clone(previous.config || {});
  }
  CRM_ENTITY_KEYS.forEach(key => {
    if (['historialEtapas', 'historialEdicionesLead', 'historialAsignaciones'].includes(key) || (!isOwner && ['resumenDiario', 'snapshotsPipeline'].includes(key))) return;
    if (previous[key] === next[key]) return;
    const before = new Map((previous[key] || []).map(item => [String(item.id), item]));
    const after = new Map((next[key] || []).map(item => [String(item.id), item]));
    const changed = [...after.values()].filter(item => before.get(String(item.id)) !== item && JSON.stringify(before.get(String(item.id))) !== JSON.stringify(item));
    changes.upserts[key] = changed.map(clone);
    changes.deletes[key] = [...before.keys()].filter(id => !after.has(id));
    changes.bases[key] = [...changed.map(item => String(item.id)), ...changes.deletes[key]].map(id => ({ id, before: before.has(id) ? clone(before.get(id)) : null }));
  });
  return changes;
}
export const hasCrmChanges = (changes) => Boolean(changes?.config) || CRM_ENTITY_KEYS.some(
  key => changes?.upserts?.[key]?.length || changes?.deletes?.[key]?.length
);

export const coreBaseApi = {
  workspaces: () => axiosInstance.get(`${BASE}/workspaces`).then(r => r.data),
  saveSeller: (seller, workspaceId) => axiosInstance.post(`${BASE}/sellers`, seller, options(workspaceId)).then(r => r.data),
  sync: (legacyState, workspaceId, sinceRevision) => axiosInstance.post(`${BASE}/sync`, { legacyState, sinceRevision }, options(workspaceId)).then(r => r.data),
  transact: (entry) => axiosInstance.post(`${BASE}/transactions`, entry, options(entry.workspaceId)).then(r => r.data),
  createEntry: (changes, workspaceId = activeWorkspace) => ({ transactionId: uuid(), workspaceId, changes, clientTimestamp: new Date().toISOString() }),
};

export const createCrmQueue = (workspaceId) => {
  const viewerId = currentUserId();
  const keyFor = () => workspaceId === undefined ? queueKey() : crmStorageKey('corebase_crm_tx_v1', workspaceId, viewerId);
  if (workspaceId === viewerId && localStorage.getItem(keyFor()) === null) {
    const legacy = readQueue('corebase_crm_tx_v1_' + viewerId);
    if (legacy.length) writeQueue(legacy.map(entry => ({ ...entry, workspaceId })), keyFor());
  }
  let sequence = 0;
  const migrate = key => {
    const legacy = readQueue(key);
    for (const entry of legacy) {
      const entryKey = key + ':' + entry.transactionId;
      if (!localStorage.getItem(entryKey)) localStorage.setItem(entryKey, JSON.stringify(entry));
    }
    // Keep the marker to prevent importing legacy changes again.
    if (legacy.length || localStorage.getItem(key) === null) writeQueue([], key);
  };
  const listAt = key => {
    migrate(key);
    const rows = [];
    for (let i = 0; i < localStorage.length; i++) {
      const entryKey = localStorage.key(i);
      if (entryKey?.startsWith(key + ':')) {
        try { rows.push(JSON.parse(localStorage.getItem(entryKey))); } catch { /* retain unreadable entries */ }
      }
    }
    return rows.sort((a, b) => String(a.queuedAt || '').localeCompare(String(b.queuedAt || '')) || (a.sequence || 0) - (b.sequence || 0));
  };
  return {
    list: () => listAt(keyFor()),
    enqueue(entry) {
      const key = keyFor(); migrate(key);
      const entryKey = key + ':' + entry.transactionId;
      if (localStorage.getItem(entryKey)) return false;
      localStorage.setItem(entryKey, JSON.stringify({ ...entry, queuedAt: new Date().toISOString(), sequence: sequence++ }));
      return true;
    },
    async flush(processEntry) {
      const key = keyFor();
      for (const entry of listAt(key)) {
        try { await processEntry(entry); }
        catch (error) { return { ok: false, pending: listAt(key).length, error }; }
        localStorage.removeItem(key + ':' + entry.transactionId);
      }
      const pending = listAt(key).length;
      return { ok: pending === 0, pending };
    },
    remove: ids => { for (const id of ids) localStorage.removeItem(keyFor() + ":" + id); },
    clear: () => {
      const key = keyFor();
      for (const entry of listAt(key)) localStorage.removeItem(key + ':' + entry.transactionId);
    },
  };
};
export const coreBaseQueue = createCrmQueue();